import {
  useQuery,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
  UseMutationOptions,
  useMutation,
} from 'react-query';
import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiResponseType } from '../types';
import { axiosInstance as apiClient } from '../rest-data-provider/utils/axios';

export const useGet = <T>(
  key: QueryKey,
  url: string,
  config?: AxiosRequestConfig,
  options?: UseQueryOptions<
    AxiosResponse<ApiResponseType<T>>,
    unknown,
    AxiosResponse<ApiResponseType<T>>
  >,
  shouldFetch: boolean = true // Default value is true
): UseQueryResult<AxiosResponse<ApiResponseType<T>>, unknown> => {
  return useQuery<AxiosResponse<ApiResponseType<T>>, unknown>(
    key,
    async () => {
      if (!shouldFetch) {
        return Promise.reject(new Error('Fetching is disabled'));
      }
      const response: AxiosResponse<ApiResponseType<T>> = await apiClient.get(
        url,
        config
      );
      if (response) {
        return response; // Return the whole AxiosResponse here
      } else {
        throw new Error('No data found');
      }
    },
    {
      ...options,
      staleTime: options?.staleTime ?? 0,
      retry: options?.retry ?? false,
      refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
      onError: (error) => {
        // eslint-disable-next-line no-console
        console.error('Error fetching data:', error);
      },
    }
  );
};

interface ApiResponse<T> {
  data: T;
}

interface MutationConfig<T, V>
  extends UseMutationOptions<AxiosResponse<ApiResponse<T>>, AxiosError, V> {
  onProgress?: (progress: number) => void;
}

const useAPI = () => {
  const usePost = <T, V>(
    url: string,
    config?: AxiosRequestConfig,
    options?: MutationConfig<T, V>
  ) => {
    return useMutation<AxiosResponse<ApiResponse<T>>, AxiosError, V>(
      async (newData: V) => {
        const response = await apiClient.post(url, newData, {
          ...config,
          onUploadProgress: (progressEvent) => {
            if (options?.onProgress && progressEvent.total) {
              const progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              options.onProgress(progress);
            }
          },
        });
        return response;
      },
      options
    );
  };

  const useDelete = <T, V>(
    url: string,
    config?: AxiosRequestConfig,
    options?: UseMutationOptions<T, AxiosError, V>
  ) => {
    return useMutation<T, AxiosError, V>(async () => {
      const response: AxiosResponse<ApiResponse<T>> = await apiClient.delete(
        url,
        config
      );
      return response.data.data;
    }, options);
  };
  const usePatch = <T, V>(
    url: string,
    config?: AxiosRequestConfig,
    options?: UseMutationOptions<T, AxiosError, V>
  ) => {
    return useMutation<T, AxiosError, V>(async (newData: V) => {
      const response: AxiosResponse<ApiResponse<T>> = await apiClient.patch(
        url,
        newData,
        config
      );
      return response.data.data;
    }, options);
  };
  return {
    usePost,
    useDelete,
    usePatch,
  };
};

export default useAPI;
